import React, { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSignupWizard } from './signup-wizard-context';
import WizardLayout from './wizard-layout';
import StepHeader from './step-header';
import ErrorAlert from './error-alert';
import { ServiceSupplierSignup } from '../../services/service-supplier-signup';
import appState from '../../state/AppStateContainer';
import BtButton from '../bt-button/bt-button';
import Field from '../../components/shared/Field/Field';
import Spinner from '../../components/shared/Spinner/Spinner';
import { NotificationManager } from 'react-notifications';

const SUPPORT_EMAIL = 'support@accurator.se';
const errorStyle: React.CSSProperties = { fontSize: '0.875rem', color: 'var(--red-700)' };
const linkStyle: React.CSSProperties = { color: 'var(--orange-500)', textDecoration: 'underline' };
const linkBtnStyle: React.CSSProperties = { ...linkStyle, fontSize: '0.875rem', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', alignSelf: 'flex-start' as const };

type ErrorKind =
  | 'verification.invalidCode'
  | 'verification.codeExpired'
  | 'verification.tooManyAttempts'
  | 'verification.orgAlreadyExists'
  | 'generic';

const StepVerify: FC = () => {
  const { t } = useTranslation('newSupplier', { keyPrefix: 'wizard' });
  const history = useHistory();
  const { state, setCurrentStep } = useSignupWizard();
  const { email } = state;

  const [sendingCode, setSendingCode] = useState(true);
  const [codeSent, setCodeSent] = useState(false);
  const [sendError, setSendError] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorKind, setErrorKind] = useState<ErrorKind | null>(null);

  const sendCode = async () => {
    setSendingCode(true);
    setSendError('');
    try {
      await ServiceSupplierSignup.sendVerification(email);
      setCodeSent(true);
    } catch {
      setSendError(t('verifySend.sendError', 'Kunde inte skicka verifieringsmail. Försök igen.'));
    } finally {
      NotificationManager.success(t('verifySend.codeSent', 'Vi har skickat en verifieringskod till {{email}}', { email } ));
      setSendingCode(false);
    }
  };

  useEffect(() => {
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = () => {
    setCode('');
    setErrorKind(null);
    sendCode();
  };

  const handleCodeChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    setCode(cleaned);
    if (errorKind === 'verification.invalidCode') setErrorKind(null);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (code.length !== 6 || submitting) return;

    setSubmitting(true);
    setErrorKind(null);

    try {
      const result = await ServiceSupplierSignup.verifyAndCreate({
        organizationNumber: state.organizationNumber,
        companyName: state.companyName,
        address: state.address,
        city: state.city,
        postalCode: state.postalCode,
        termsAcceptedAt: state.termsAcceptedAt as string,
        areaIds: state.areaIds,
        places: state.places,
        name: state.name,
        title: state.title,
        phoneNumber: state.phoneNumber,
        email: state.email,
        password: state.password,
        addAsContact: state.addAsContact,
        colleagues: state.colleagues,
        verificationCode: code,
        sessionId: state.sessionId,
      });

      await appState.setToken(
        { userData: result.userData, organizationData: result.organizationData },
        false,
      );
      setCurrentStep(6);
      window.location.href = `/${result.orgSlug}/supplier-presentation`;
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err as Error)?.message || '';

      const KNOWN_ERRORS: ErrorKind[] = ['verification.invalidCode', 'verification.codeExpired', 'verification.tooManyAttempts', 'verification.orgAlreadyExists'];
      setErrorKind(KNOWN_ERRORS.find(k => message.includes(k)) || 'generic');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = code.length === 6 && !submitting;
  const codeFieldError = errorKind === 'verification.invalidCode'
    ? t('verifyCode.invalidCode', 'Ogiltig kod. Försök igen.') : undefined;

  return (
    <WizardLayout>
      <div className="flex flex-col gap-6">
        <StepHeader
          title={t('verify.title', 'Verifiera din e-post')}
          stepNumber={6}
          description={t('verify.description', 'Ange koden vi skickat till din e-post')}
        />

        {/* Status message */}
        {sendingCode && (
          <div className="flex items-center gap-3">
            <Spinner progress={0} size="xs" />
            <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
              {t('verifySend.sending', 'Skickar verifieringskod...')}
            </span>
          </div>
        )}

        {sendError && <ErrorAlert title={sendError} />}

        {codeSent && !sendingCode && (
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>
            {t('verifySend.codeSent', 'Vi har skickat en verifieringskod till')} <strong>{email}</strong>
          </p>
        )}

        {/* Code input — always visible once first send completes or fails */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <Field
            id="verification-code"
            value={code}
            onChange={handleCodeChange}
            placeholder="000000"
            label={t('verifyCode.codeLabel', 'Verifieringskod')}
            disabled={submitting}
            errorMessage={codeFieldError}
          />

          {errorKind === 'verification.codeExpired' && (
            <div className="flex flex-col gap-2">
              <p style={errorStyle}>
                {t('verifyCode.codeExpired', 'Koden har gått ut.')}
              </p>
              <button type="button" onClick={handleResend} style={linkBtnStyle}>
                {t('verifyCode.sendNewCode', 'Skicka ny kod')}
              </button>
            </div>
          )}

          {errorKind === 'verification.tooManyAttempts' && (
            <div className="flex flex-col gap-1">
              <p style={errorStyle}>
                {t('verifyCode.tooManyAttempts', 'För många försök. Begär en ny kod.')}
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                {t('verifyCode.needHelp', 'Behöver du hjälp?')}{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} style={linkStyle}>
                  {t('verifyCode.contactSupport', 'Kontakta support')}
                </a>
              </p>
            </div>
          )}

          {errorKind === 'verification.orgAlreadyExists' && (
            <div className="flex flex-col gap-1">
              <p style={errorStyle}>
                {t('verifyCode.orgAlreadyExists', 'Denna organisation har precis registrerats.')}
              </p>
              <a href="/login" style={linkBtnStyle}>
                {t('verifyCode.goToLogin', 'Gå till inloggning')}
              </a>
            </div>
          )}

          {errorKind === 'generic' && (
            <p style={errorStyle}>
              {t('verifyCode.genericError', 'Något gick fel. Försök igen eller')}{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} style={linkStyle}>
                {t('verifyCode.contactSupport', 'kontakta support')}
              </a>.
            </p>
          )}

          <div className="flex items-center justify-between">
            <BtButton color="white" onClick={() => history.push('/supplier-sign-up/invite')}>
              &larr; {t('layout.back', 'Tillbaka')}
            </BtButton>
            <BtButton onClick={handleSubmit} disabled={!canSubmit} loaderShow={submitting}>
              {t('verifyCode.verify', 'Verifiera')} &rarr;
            </BtButton>
          </div>
        </form>

        {/* Resend link */}
        <div className="text-center">
          <button type="button" onClick={handleResend} disabled={sendingCode} style={{ fontSize: '0.875rem', color: 'var(--gray-500)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            {t('verifyCode.resend', 'Fick du ingen kod? Skicka igen')}
          </button>
        </div>
      </div>
    </WizardLayout>
  );
};

export default StepVerify;
